import { useState } from 'react'
import {
  Box,
  Flex,
  Text,
  TimerIcon,
  CloseIcon,
  IconButton,
  ArrowDownCircle,
  CheckmarkIcon,
  Link,
  Locked,
  ArrowRight,
} from 'vvs-uikit'
import { History } from 'history'
import useEndOfUTCDayCountdown from 'hooks/useEndOfUTCDayCountdown'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import { Path } from 'config/constants/paths'
import { useTranslation } from 'contexts/Localization'
import { Gachapon, GACHAPON_QUESTS_REQUIREMENT } from 'state/vvsgotchi/hooks/useGachapon'
import { formatTime } from 'utils/dayjs'

import { GachaponStep } from '../types'

import {
  GachaponModalFlex,
  ModalTitleFlex,
  ModalTitleIcon,
  ModalCloseButton,
  CountdownLabel,
  TaskButton,
  TaskStatusLabel,
  GachaponCtaButton,
  AnimationContainer,
  AnimatedPixelatedImage,
} from './styles'

const GachaponRequirements: React.FC<{
  onDismiss: () => any
  setStep: (step: GachaponStep) => any
  gachapon: Gachapon
  history: History
}> = ({ onDismiss, setStep, gachapon, history }) => {
  const { t } = useTranslation()
  const timeExcerptMap = useEndOfUTCDayCountdown()
  const [animationLoaded, setAnimationLoaded] = useState(false)

  const playAnimation = animationLoaded && gachapon.isGachaReady

  return (
    <GachaponModalFlex>
      <ModalTitleFlex p={3} bg="grass" bottomLine>
        <ModalTitleIcon src="/images/vvsGotchi/gachapon-icon.png" />
        <ModalCloseButton>
          <IconButton variant="text" onClick={onDismiss}>
            <CloseIcon fill="black" />
          </IconButton>
        </ModalCloseButton>
        <Flex alignItems="end">
          <Box flex="1">
            <Text color="black" fontSize="13px" fontWeight="700">
              {t('%startDate% - %endDate%', {
                startDate: formatTime(gachapon.gachaponStart, 'D MMM'),
                endDate: formatTime(gachapon.gachaponEnd, 'D MMM YYYY'),
              })}
            </Text>
            <Text color="black" fontSize="20px" fontWeight="600">
              {t('Daily Lucky Draw')}
            </Text>
          </Box>
          <CountdownLabel>
            <TimerIcon width="14px" fill="white" pt="2px" />
            <Text color="white" fontSize="13px" fontWeight="700" pl="3px">
              {timeExcerptMap.h > 0
                ? t('%h%h left today', timeExcerptMap)
                : timeExcerptMap.m > 0
                ? t('%m%m left today', timeExcerptMap)
                : t('%s%s left today', timeExcerptMap)}
            </Text>
          </CountdownLabel>
        </Flex>
        <Text color="black" fontSize="13px" fontWeight="700" mt={2}>
          {t('Complete daily missions to earn a EXP/ HP Lucky Draw!')}
          <Link
            ml={1}
            fontWeight="700"
            inline
            underline="always"
            href="https://docs.vvs.finance/product-guides/vvs-gotchi"
            target="_blank"
            rel="noreferrer"
          >
            {t('Learn More')}
          </Link>
        </Text>
      </ModalTitleFlex>
      <Flex flexDirection="column" alignItems="center" p={3} gap="12px">
        <Flex gap="8px">
          <TaskBtn isCompleted={gachapon.isTeamFedToday} onClick={() => history.push(Path.VVS_GOTCHI_TEAM_FEED)}>
            <Text color="black" fontSize="13px" fontWeight="700" textAlign="left">
              {t('Feed the whole team →')}
            </Text>
            <PixelatedImage src="/images/vvsGotchi/tableware.png" width="10.8px" />
          </TaskBtn>
          <TaskBtn
            isCompleted={gachapon.hasCompletedQuestRequirement}
            onClick={() => history.push(Path.VVS_GOTCHI_QUEST, { focus: 'quests' })}
          >
            <Text color="black" fontSize="13px" fontWeight="700" textAlign="left">
              {t('Complete any two different quests (%completed%/%requires%)', {
                completed: gachapon.todayDifferentQuestCount,
                requires: GACHAPON_QUESTS_REQUIREMENT,
              })}
            </Text>
            <PixelatedImage src="/images/vvsGotchi/flag.png" width="15.2px" />
          </TaskBtn>
        </Flex>

        <ArrowDownCircle width="32px" />

        <GachaponCtaButton disabled={!gachapon.isGachaReady} onClick={() => setStep('select')}>
          <Flex alignItems="center" px={2} py={3} gap="4px">
            <AnimationContainer
              aspect="800/1080"
              height="140px"
              bg={!playAnimation ? '/images/vvsGotchi/gachapon-machine.png' : null}
            >
              <AnimatedPixelatedImage
                src="/images/vvsGotchi/gachapon-animation.png"
                onLoad={() => setAnimationLoaded(true)}
                show={playAnimation}
              />
            </AnimationContainer>
            <Flex flexDirection="column">
              <Flex>
                <Text color="black" fontSize="20px" fontWeight="600" pr={2}>
                  {t('Try my Luck')}
                </Text>
                {gachapon.isGachaReady ? (
                  <ArrowRight width="12px" fill="black" />
                ) : (
                  <Locked width="16px" fill="black" />
                )}
              </Flex>
              <Text
                color="black"
                fontSize="13px"
                textAlign="left"
                pr={2}
                dangerouslySetInnerHTML={{
                  __html: t('Chance to win extra <b>EXP</b> or <b>Health Points</b>!'),
                }}
              />
            </Flex>
          </Flex>

          {!gachapon.isGachaReady && (
            <TaskStatusLabel type="pending">
              <Locked width="12px" fill="white" />
              <Text color="lightBeige" fontSize="11px" pl={2}>
                {t('Fulfill the requirements above to unlock')}
              </Text>
            </TaskStatusLabel>
          )}
        </GachaponCtaButton>
      </Flex>
    </GachaponModalFlex>
  )
}

export default GachaponRequirements

const TaskBtn: React.FC<{ isCompleted: boolean; onClick?: () => any }> = ({ isCompleted, onClick, children }) => {
  const { t } = useTranslation()
  return (
    <TaskButton isCompleted={isCompleted} disabled={isCompleted} onClick={onClick}>
      {isCompleted && (
        <TaskStatusLabel type="completed">
          <CheckmarkIcon fill="black" width="16px" pr={1} />
          <Text color="black" fontSize="11px">
            {t('Completed')}
          </Text>
        </TaskStatusLabel>
      )}
      <Flex flex="1" alignItems="center">
        {children}
      </Flex>
    </TaskButton>
  )
}
